import _, { clamp, memoize } from "lodash";
import { Cluster } from "../interfaces";
import { Viewport } from "pixi-viewport";
import { extent } from "d3-array";
import { Point } from "../types";
import { polygonHull } from "d3";
import {Point as PixiPoint} from "pixi.js";

export const calculateControlPoint = (start: any, end: any, curvatureFactor = 0.3) => {
  const { x: x1, y: y1 } = start;
  const { x: x2, y: y2 } = end;

  const deltaX = x2 - x1;
  const deltaY = y2 - y1;

  if (deltaX === 0 && deltaY === 0) {
    // If start and end are the same point, return the point itself
    return { x: x1, y: y1 };
  }

  const angle = Math.atan2(deltaY, deltaX);
  const perpendicularAngle = angle + Math.PI / 2; // 90 degrees

  // Distance for control point offset
  const absDeltaX = deltaX >= 0 ? deltaX : -deltaX;
  const absDeltaY = deltaY >= 0 ? deltaY : -deltaY;
  const minDistance = absDeltaX < absDeltaY ? absDeltaX : absDeltaY;
  const distance = minDistance * curvatureFactor;

  const midX = (x1 + x2) * 0.5;
  const midY = (y1 + y2) * 0.5;

  const cosPerp = Math.cos(perpendicularAngle);
  const sinPerp = Math.sin(perpendicularAngle);

  return {
    x: midX + distance * cosPerp,
    y: midY + distance * sinPerp,
  };
};

export const calculateCentroid = (items: any) => {
  if (items.length === 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: _.meanBy(items, "x"),
    y: _.meanBy(items, "y"),
  };
};
export const calculateCentroidForNode = (node: any) => {
  if (!node.children || node.children.length === 0) {
    return { x: node.x, y: node.y };
  }
  return {
    x: _.meanBy(node.children, "x"),
    y: _.meanBy(node.children, "y"),
  };
};

export const findIntersectionPoint = (
  centroid: { x: number; y: number },
  child: { x: number; y: number },
  hullPoints: Array<{ x: number; y: number }>,
): { x: number; y: number } | null => {
  const direction = {
    x: child.x - centroid.x,
    y: child.y - centroid.y,
  };

  const magnitude = Math.sqrt(direction.x ** 2 + direction.y ** 2);
  const unitDirection = {
    x: direction.x / magnitude,
    y: direction.y / magnitude,
  };

  // Iterate through hull edges to find intersection
  for (let i = 0; i < hullPoints.length; i++) {
    const start = hullPoints[i];
    const end = hullPoints[(i + 1) % hullPoints.length];

    // Line segment from centroid in the direction of the child
    const p1 = centroid;
    const p2 = {
      x: centroid.x + unitDirection.x * 10000,
      y: centroid.y + unitDirection.y * 10000,
    };

    // Current hull edge
    const p3 = start;
    const p4 = end;

    const denom = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x);
    if (denom === 0) continue; // Parallel lines

    const t =
      ((p1.x - p3.x) * (p3.y - p4.y) - (p1.y - p3.y) * (p3.x - p4.x)) / denom;
    const u =
      -((p1.x - p2.x) * (p1.y - p3.y) - (p1.y - p2.y) * (p1.x - p3.x)) / denom;

    if (t >= 0 && 0 <= u && u <= 1) {
      return {
        x: p1.x + t * (p2.x - p1.x),
        y: p1.y + t * (p2.y - p1.y),
      };
    }
  }

  return null; // No intersection found
};



/**
 * Efficiently calculates the bounding box of a cluster using memoized d3.extent.
 * Memoization prevents redundant calculations for unchanged clusters.
 */
export const calculateBoundingBox = memoize((cluster: Cluster) => {
  // Early return for empty clusters to avoid unnecessary computation
  if (!cluster.children?.length) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  // Use d3.extent for efficient min/max calculation
  const xExtent = extent(cluster.children, (child) => child.x ?? 0) as [number, number];
  const yExtent = extent(cluster.children, (child) => child.y ?? 0) as [number, number];

  return {
    minX: xExtent[0],
    maxX: xExtent[1],
    minY: yExtent[0],
    maxY: yExtent[1],
  };
}, (cluster) => cluster.id); // Memoize based on cluster ID

/**
 * Determines the optimal zoom level to fit the cluster within the viewport.
 *
 * @param {Cluster} cluster - The cluster to fit.
 * @param {Viewport} viewport - The PIXI-Viewport instance.
 * @param {number} padding - Padding in pixels around the cluster.
 * @param {number} maxZoom - The maximum allowed zoom level.
 * @returns { { zoom: number; center: Point; } } - The optimal zoom level and center position.
 */
export const calculateOptimalZoomAndCenter = (
  cluster: Cluster,
  viewport: Viewport,
  padding: number = 50,
  maxZoom: number = 8,
) => {
  const { minX, maxX, minY, maxY } = calculateBoundingBox(cluster);

  const clusterWidth = maxX - minX;
  const clusterHeight = maxY - minY;

  if (clusterWidth === 0 || clusterHeight === 0) {
    // Handle edge case where cluster has no area
    return {
      zoom: maxZoom,
      center: { x: minX, y: minY },
    };
  }

  const { screenWidth: viewportWidth, screenHeight: viewportHeight } = viewport;

  const zoomX = (viewportWidth - padding * 2) / clusterWidth;
  const zoomY = (viewportHeight - padding * 2) / clusterHeight;
  const targetZoom = clamp(Math.min(zoomX, zoomY), 0.1, maxZoom); // Ensures zoom is between 0.1 and maxZoom

  // Calculate centroid of the cluster
  const centerX = minX + clusterWidth / 2;
  const centerY = minY + clusterHeight / 2;

  return {
    zoom: targetZoom,
    center: { x: centerX, y: centerY },
  };
};

export   const generateConvexHull = (points: Point[]): Point[] => {
  const len = points.length;

  if (len < 3) {
    return points;
  }

  // Preallocate the formattedPoints array
  const formattedPoints: [number, number][] = new Array(len);
  for (let i = 0; i < len; i++) {
    formattedPoints[i] = [points[i].x, points[i].y];
  }

  const hull = polygonHull(formattedPoints);

  if (!hull) {
    return points;
  }

  // Preallocate the hullPoints array
  const hullLen = hull.length;
  const hullPoints: Point[] = new Array(hullLen);
  for (let i = 0; i < hullLen; i++) {
    hullPoints[i] = { x: hull[i][0], y: hull[i][1] };
  }

  return hullPoints;
};

export const calculateCategoryControlPoints = (
  centroid: PixiPoint,
  index: number,
  total: number,
  radius: number = 100,
): PixiPoint => {
  const angle = ((2 * Math.PI) / total) * index;
  const x = centroid.x + radius * Math.cos(angle);
  const y = centroid.y + radius * Math.sin(angle);
  return new PixiPoint(x, y);
};
